/**
 * AI 利用量（Issue #475 Phase 1 / Phase 5）の Mock データ。
 *
 * 本番の api-server は AI サーバーが返した値をそのまま写したスナップショット
 * （tenant_ai_usage_snapshots）を返すだけで、丸め・0 クランプ・導出は行わない。
 * この Mock は「AI サーバー + api-server のスナップショット」の役を兼ねるため、
 * remaining が負値（上限超過）になるテナントを最初から用意している。
 *
 * 応答の型は features/ai-usage/ai-usage.models.ts（openapi 契約）から、軸の語彙は
 * core/utils/ai-usage.utils.ts（軸の定義元）から import する。
 */

import { isUsageExhausted } from '../../features/ai-usage/ai-usage.models';

import type { AiUsageType } from '../../core/utils/ai-usage.utils';
import type { AiUsageItemApiResponse } from '../../features/ai-usage/ai-usage.models';

/**
 * Mock が保持する利用量項目。
 *
 * openapi 契約の `type` は将来の軸追加に備えて `string` で開いているが、Mock は
 * 「AI サーバーが実際に返す既知の軸」だけを持つため union に閉じる
 * （軸ごとの消費量テーブルを網羅的に引けるようにするため）。
 */
type MockAiUsageItem = AiUsageItemApiResponse & { type: AiUsageType };

/** テナントごとに保持する Mock 状態 */
type TenantAiUsageMockState = {
  /** 保存済みスナップショット（2 軸そろって返る） */
  usages: MockAiUsageItem[];
  /** スナップショット取得時刻（ISO8601） */
  fetchedAt: string;
  /**
   * refresh 時の AI サーバー照会が失敗するテナントを模擬するフラグ。
   * true の場合、refresh は保存値をそのまま stale: true で返す
   * （api_keys 未登録・AI サーバー通信エラーのケース）。
   */
  refreshFails: boolean;
};

/** 残量に余裕があるテナント（me.mock の GX テナント） */
export const AI_USAGE_MOCK_TENANT_ID_WITHIN_LIMIT = 'tenant-mock-001';

/** 実行費用が上限超過（remaining 負値）のテナント（me.mock の GX事務局 テナント） */
export const AI_USAGE_MOCK_TENANT_ID_OVER_LIMIT = 'tenant-mock-002';

/**
 * API キー未登録で refresh が失敗するテナント。
 * me.mock には含めていないため画面から選択できない。ハンドラのテストと
 * 「refresh 失敗でもエラーにしない」挙動の確認用。
 */
export const AI_USAGE_MOCK_TENANT_ID_NO_API_KEY = 'tenant-mock-003';

/**
 * refresh 1 回あたりに進む消費量（軸ごと）。
 * 二進小数で誤差が出ない 0.25 刻みの値を使い、テストで差分を厳密比較できるようにしている。
 */
const REFRESH_CONSUMED_DELTA: Record<AiUsageType, number> = {
  execution_cost_usd: 12.5,
  extraction_cost_usd: 6.25,
};

/**
 * Mock 状態の初期値を生成する。
 * refresh でミュータブルに書き換わるため、reset 時に毎回新しいオブジェクトを作る。
 */
function createInitialAiUsageState(): Record<
  string,
  TenantAiUsageMockState | undefined
> {
  return {
    // 残量に余裕がある正常系
    [AI_USAGE_MOCK_TENANT_ID_WITHIN_LIMIT]: {
      usages: [
        {
          type: 'execution_cost_usd',
          unit: 'usd',
          granted: 1000,
          consumed: 312.5,
          remaining: 687.5,
        },
        {
          type: 'extraction_cost_usd',
          unit: 'usd',
          granted: 500,
          consumed: 128.25,
          remaining: 371.75,
        },
      ],
      fetchedAt: '2026-08-25T01:23:45.000Z',
      refreshFails: false,
    },
    // 実行費用が上限超過（remaining 負値）。抽出費用は残量僅少
    [AI_USAGE_MOCK_TENANT_ID_OVER_LIMIT]: {
      usages: [
        {
          type: 'execution_cost_usd',
          unit: 'usd',
          granted: 100,
          consumed: 137.75,
          remaining: -37.75,
        },
        {
          type: 'extraction_cost_usd',
          unit: 'usd',
          granted: 50,
          consumed: 49.5,
          remaining: 0.5,
        },
      ],
      fetchedAt: '2026-08-24T22:10:00.000Z',
      refreshFails: false,
    },
    // API キー未登録で refresh が失敗するテナント（保存値のみ返る）
    [AI_USAGE_MOCK_TENANT_ID_NO_API_KEY]: {
      usages: [
        {
          type: 'execution_cost_usd',
          unit: 'usd',
          granted: 800,
          consumed: 640.25,
          remaining: 159.75,
        },
        {
          type: 'extraction_cost_usd',
          unit: 'usd',
          granted: 400,
          consumed: 128.5,
          remaining: 271.5,
        },
      ],
      fetchedAt: '2026-08-20T09:00:00.000Z',
      refreshFails: true,
    },
  };
}

/**
 * テナントごとの Mock 状態。refresh でミュータブルに更新される。
 * エントリが無いテナント（値が undefined）は「スナップショット未保存」を表す。
 */
let MOCK_AI_USAGE_STATE: Record<string, TenantAiUsageMockState | undefined> =
  createInitialAiUsageState();

/**
 * 保存済みスナップショットを取得する。
 * 未保存テナント（エントリなし）の場合は undefined を返す。
 */
export function getAiUsageMockState(
  tenantId: string,
): TenantAiUsageMockState | undefined {
  return MOCK_AI_USAGE_STATE[tenantId];
}

/**
 * AI サーバーへの同期照会を模擬してスナップショットを更新する。
 *
 * 消費が進んだ新しい値を「AI サーバーの応答」として作り、fetchedAt を現在時刻に更新する。
 * remaining は granted - consumed から導出せず、AI サーバー応答をそのまま写す本番の挙動に
 * 合わせて前回値から delta を引いた値を用いる（負値もそのまま保持する）。
 *
 * # 実 API との差（Mock の割り切り）
 *
 * - 実 API は照会に成功すると応答内容で **全置換** する（応答に無い軸は削除）。
 *   Mock は AI サーバーの役を保存値で兼ねているため軸の増減は起こさない
 * - 実 API は「スナップショット未保存 + API キー有効」でも照会して値を返すが、
 *   Mock は AI サーバー応答の元データを持たないため照会失敗（縮退）として扱う
 *
 * @param tenantId テナント ID
 * @param now 取得時刻（テスト用に差し替え可能）
 * @returns 同期照会に成功したか（false の場合、保存値は変更されない）
 */
export function refreshAiUsageMockState(
  tenantId: string,
  now: Date = new Date(),
): boolean {
  const state = MOCK_AI_USAGE_STATE[tenantId];

  // スナップショット未保存テナントは Mock 側に AI サーバー応答を持たないため失敗扱い
  if (!state) {
    return false;
  }

  // API キー未登録・通信エラーの模擬（保存値は据え置き）
  if (state.refreshFails) {
    return false;
  }

  state.usages = state.usages.map((usage) => {
    const delta = REFRESH_CONSUMED_DELTA[usage.type];
    return {
      ...usage,
      consumed: usage.consumed + delta,
      remaining: usage.remaining - delta,
    };
  });
  state.fetchedAt = now.toISOString();

  return true;
}

/**
 * 上限に到達している（remaining <= 0）軸を返す（Issue #475 Phase 5）。
 *
 * 本番の api-server は AI サーバーが返した 402 の内訳をそのまま `exceeded[]` に載せるが、
 * Mock には AI サーバーが居ないため、保存済みスナップショットから「もう使えない軸」を
 * 導出して 402 相当の内訳を組み立てる。
 * スナップショット未保存のテナントは「上限不明」であり超過とは判定しない（空配列）。
 *
 * @param tenantId テナント ID
 * @returns 上限到達している軸（AI サーバー応答の写しとして無加工。負値もそのまま）
 */
export function findExhaustedAiUsages(
  tenantId: string,
): AiUsageItemApiResponse[] {
  const state = MOCK_AI_USAGE_STATE[tenantId];
  if (!state) {
    return [];
  }
  // Mock 状態を呼び出し側から書き換えられないようコピーして返す
  return state.usages.filter(isUsageExhausted).map((usage) => ({ ...usage }));
}

/**
 * 指定テナントの refresh 成否を切り替える。
 * Mock 画面で「照会失敗でも保存値 + stale で表示される」挙動を確認するための操作口。
 */
export function setAiUsageMockRefreshFailure(
  tenantId: string,
  refreshFails: boolean,
): void {
  const state = MOCK_AI_USAGE_STATE[tenantId];
  if (!state) {
    return;
  }
  state.refreshFails = refreshFails;
}

/**
 * Mock 状態を初期値に戻す（テストの前処理用）
 */
export function resetAiUsageMockData(): void {
  MOCK_AI_USAGE_STATE = createInitialAiUsageState();
}
